import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { FaceSignCapture } from "./FaceSignCapture";
import {
  GraduationCap, KeyRound, Loader2, CheckCircle2, Clock,
  ShieldCheck, AlertTriangle, FileText, Phone, Mail, ScanFace,
} from "lucide-react";

const fmtPhoneMask = (p) => {
  if (!p) return "";
  const digits = p.replace(/\D/g, "");
  if (digits.length < 4) return p;
  const last4 = digits.slice(-4);
  return `(**) *****-${last4}`;
};

const maskEmail = (e) => {
  if (!e || !e.includes("@")) return e || "";
  const [user, domain] = e.split("@");
  if (user.length <= 1) return `${user}@${domain}`;
  return `${user[0]}***@${domain}`;
};

/**
 * EmployeeAttendanceConvocations — bloqueante quando há convocação
 * pendente para o funcionário em uma Lista de Presença Digital.
 *
 * Fluxo: ver convocação → ler conteúdo do curso → "Li e concordo"
 * → confirmar telefone → enviar OTP → digitar OTP → assinar.
 */
export default function EmployeeAttendanceConvocations({ refreshKey = 0, onSigned } = {}) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openToken, setOpenToken] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/employee/pending-attendances`, {
        params: { token },
      });
      setItems(res.data?.items || []);
    } catch (e) {
      // silent — not critical
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Refresh when the QuestionarioBlocker signs the list on our behalf.
  useEffect(() => {
    const h = () => load();
    window.addEventListener("avi-attendances-refresh", h);
    return () => window.removeEventListener("avi-attendances-refresh", h);
  }, [load]);

  if (loading) return null;
  if (!items.length) return null;

  return (
    <>
      <div className="space-y-2" data-testid="employee-attendance-list">
        {items.map((it) => {
          const isDecl = it.course_key === "declaracao_act";
          return (
          <motion.div
            key={it.token}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-emerald-300 bg-gradient-to-r from-emerald-50 to-white">
              <CardContent className="p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 flex items-start gap-3">
                  <div className="bg-emerald-600/10 rounded-lg p-2 mt-0.5">
                    {isDecl
                      ? <FileText className="w-5 h-5 text-emerald-700" />
                      : <GraduationCap className="w-5 h-5 text-emerald-700" />}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase font-semibold tracking-wider text-emerald-700 mb-0.5">
                      {isDecl ? "Documento para assinatura" : "Convocação para treinamento"}
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      {it.course_name}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {isDecl
                        ? <>Leia o documento e assine digitalmente</>
                        : <>Treinamento em <strong>{it.training_date}</strong></>}
                      {it.expires_at && (
                        <>
                          {" "}
                          •{" "}
                          <span className="text-amber-700">
                            <Clock className="inline w-3 h-3 mr-0.5" />
                            até {new Date(it.expires_at).toLocaleString("pt-BR")}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  data-testid={`attendance-sign-${it.token}`}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => setOpenToken(it.token)}
                >
                  {isDecl ? "Ler e assinar" : "Assinar agora"}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
          );
        })}
      </div>

      <AttendanceSignDialog
        tokenId={openToken}
        onClose={() => setOpenToken(null)}
        onSigned={() => {
          setOpenToken(null);
          load();
          // Notify parent so other LMS panels (course-trainings) refresh too.
          if (typeof onSigned === "function") onSigned();
        }}
      />
    </>
  );
}


export function AttendanceSignDialog({ tokenId, onClose, onSigned }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("preview"); // preview | otp | face
  const [agreed, setAgreed] = useState(false);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [signing, setSigning] = useState(false);
  // 'sms' | 'email' — which channel to deliver the OTP through.
  const [channel, setChannel] = useState("sms");
  // Set after send-otp so we can render a friendly hint on step="otp".
  const [sentTo, setSentTo] = useState("");

  useEffect(() => {
    if (!tokenId) {
      setData(null); setStep("preview"); setAgreed(false); setCode("");
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/employee/attendances/${tokenId}`, { params: { token } })
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Erro ao abrir convocação");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [tokenId, token, onClose]);

  const sendOtp = async () => {
    if (channel === "email" && !data?.email) {
      toast.error("Você não possui e-mail cadastrado. Escolha SMS ou peça ao administrador para cadastrar.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/employee/attendances/${tokenId}/send-otp`,
        {},
        { params: { token, channel } }
      );
      setSentTo(res.data?.to || "");
      toast.success(
        channel === "email"
          ? "Código enviado por e-mail"
          : "Código enviado por SMS"
      );
      setStep("otp");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar código");
    } finally {
      setSending(false);
    }
  };

  const confirmOtp = async () => {
    if (!/^\d{4,6}$/.test(code.trim())) {
      toast.error("Código inválido");
      return;
    }
    setSigning(true);
    try {
      await axios.post(
        `${API}/employee/attendances/${tokenId}/confirm-otp`,
        { code: code.trim() },
        { params: { token } }
      );
      toast.success("Presença assinada com sucesso!");
      onSigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Código inválido ou expirado");
    } finally {
      setSigning(false);
    }
  };

  if (!tokenId) return null;

  const isDecl = data?.course?.key === "declaracao_act";
  const docUrl = isDecl
    ? `${API}/employee/attendances/${tokenId}/document?token=${encodeURIComponent(token)}`
    : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        // Block ESC + outside-click — must complete or explicit cancel.
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="attendance-sign-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDecl
              ? <FileText className="w-5 h-5 text-emerald-600" />
              : <GraduationCap className="w-5 h-5 text-emerald-600" />}
            {isDecl
              ? "Assinatura de documento"
              : "Confirmação de presença em treinamento"}
          </DialogTitle>
          <DialogDescription>
            {step === "preview"
              ? isDecl
                ? "Leia o documento abaixo e escolha como deseja assinar."
                : "Leia o conteúdo do treinamento e escolha como deseja assinar."
              : step === "face"
                ? "Posicione seu rosto dentro da moldura e tire a foto."
                : channel === "email"
                  ? "Digite o código que enviamos para o seu e-mail."
                  : "Digite o código que enviamos por SMS para o seu telefone."}
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : step === "preview" ? (
          <div className="space-y-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-500 font-medium">
                {isDecl ? "DOCUMENTO" : "CURSO"}
              </div>
              <div className="text-base font-bold text-slate-900">
                {data.course.nome}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                {isDecl ? (
                  <>Assinatura digital com validade jurídica (OTP)</>
                ) : (
                  <>
                    Carga horária: <strong>{data.course.carga_horaria}</strong>
                    {" "}•{" "}
                    Data do treinamento: <strong>{data.training_date}</strong>
                  </>
                )}
              </div>
            </div>

            {isDecl && (
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="text-xs font-medium text-slate-700 flex items-center gap-1">
                    <FileText className="w-3.5 h-3.5" /> Leia o documento antes de assinar
                  </span>
                  <a
                    href={docUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-emerald-700 hover:underline"
                    data-testid="attendance-open-document-link"
                  >
                    Abrir em tela cheia
                  </a>
                </div>
                <iframe
                  src={docUrl}
                  title="Documento para assinatura"
                  className="w-full h-80 bg-slate-100"
                  data-testid="attendance-document-iframe"
                />
              </div>
            )}

            {!isDecl && data.course.conteudo?.length > 0 && (
              <div className="rounded-md border border-slate-200 p-3">
                <div className="text-xs font-medium text-slate-700 mb-1.5 flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" /> Conteúdo programático
                </div>
                <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                  {data.course.conteudo.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
              <AlertTriangle className="inline w-3.5 h-3.5 mr-1" />
              {isDecl
                ? `Ao confirmar abaixo, você assina digitalmente a
              DECLARAÇÃO DE CONHECIMENTO DE ACORDO COLETIVO DE TRABALHO.
              Esta assinatura tem valor jurídico (assinatura eletrônica
              avançada — MP 2.200-2/2001 e Lei 14.063/2020). Seu IP e
              dispositivo serão registrados.`
                : `Ao confirmar abaixo, você declara ter participado deste
              treinamento. Esta confirmação tem valor jurídico
              (assinatura eletrônica avançada — MP 2.200-2/2001 e Lei
              14.063/2020). Seu IP e dispositivo serão registrados.`}
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="att-agree"
                checked={agreed}
                onCheckedChange={setAgreed}
                data-testid="attendance-agree-checkbox"
              />
              <Label htmlFor="att-agree" className="text-sm leading-snug">
                {isDecl ? (
                  <><strong>Li e concordo</strong> com o teor da declaração
                  apresentada acima e declaro conhecimento do Acordo
                  Coletivo de Trabalho e de suas cláusulas.</>
                ) : (
                  <><strong>Li e concordo</strong> com o conteúdo apresentado e
                  declaro minha participação no treinamento.</>
                )}
              </Label>
            </div>

            <div className="rounded-md border-2 border-indigo-200 bg-indigo-50/60 p-2.5">
              <button
                type="button"
                disabled={!agreed}
                onClick={() => setStep("face")}
                className="w-full flex items-center justify-center gap-2 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm py-2.5 transition"
                data-testid="attendance-face-btn"
              >
                <ScanFace className="w-5 h-5" />
                Assinar com meu rosto (mais fácil)
              </button>
              <div className="text-[10px] text-indigo-900/70 mt-1 text-center">
                Sem código: tire uma selfie e pronto. Marque "Li e concordo" acima para liberar.
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] text-slate-400 uppercase">ou receba um código</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1.5">
                Como deseja receber o código?
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setChannel("sms")}
                  className={`text-left rounded-md border p-2 transition ${
                    channel === "sms"
                      ? "border-emerald-600 bg-white shadow-sm"
                      : "border-slate-200 bg-white/50 hover:bg-white"
                  }`}
                  data-testid="attendance-channel-sms"
                >
                  <div className="text-xs font-semibold text-slate-900 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" /> SMS
                  </div>
                  <div className="text-[10px] text-slate-600 mt-0.5">
                    {fmtPhoneMask(data.telefone) || "sem telefone cadastrado"}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setChannel("email")}
                  disabled={!data.email}
                  className={`text-left rounded-md border p-2 transition disabled:opacity-60 disabled:cursor-not-allowed ${
                    channel === "email"
                      ? "border-emerald-600 bg-white shadow-sm"
                      : "border-slate-200 bg-white/50 hover:bg-white"
                  }`}
                  data-testid="attendance-channel-email"
                >
                  <div className="text-xs font-semibold text-slate-900 flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> E-mail
                  </div>
                  <div className="text-[10px] text-slate-600 mt-0.5 truncate">
                    {data.email
                      ? maskEmail(data.email)
                      : "sem e-mail cadastrado"}
                  </div>
                </button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5">
                {channel === "email"
                  ? "O código será enviado para o e-mail cadastrado em seu perfil."
                  : "O código será enviado por SMS para o número cadastrado. Para alterar, atualize no menu de perfil."}
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                disabled={!agreed || sending || (channel === "email" && !data.email)}
                onClick={sendOtp}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="attendance-send-otp-btn"
              >
                {sending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                {channel === "email" ? "Enviar código por e-mail" : "Enviar código por SMS"}
              </Button>
            </DialogFooter>
          </div>
        ) : step === "face" ? (
          <FaceSignCapture
            tokenId={tokenId}
            token={token}
            onSigned={onSigned}
            onCancel={() => setStep("preview")}
          />
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <ShieldCheck className="inline w-4 h-4 mr-1 text-emerald-700" />
              <span className="text-sm text-emerald-900">
                {channel === "email"
                  ? `Enviamos um código de 6 dígitos para ${sentTo || "seu e-mail"}.`
                  : "Enviamos um código de 6 dígitos para seu telefone."}
              </span>
            </div>
            <div>
              <Label htmlFor="att-otp" className="text-sm">
                <KeyRound className="inline w-3.5 h-3.5 mr-1" />
                {channel === "email" ? "Código recebido por e-mail" : "Código recebido por SMS"}
              </Label>
              <Input
                id="att-otp"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                maxLength={6}
                placeholder="123456"
                className="text-center text-2xl font-mono tracking-widest mt-1.5"
                data-testid="attendance-otp-input"
                autoFocus
              />
            </div>
            <button
              className="text-xs text-emerald-700 hover:underline"
              onClick={sendOtp}
              disabled={sending}
              type="button"
            >
              Não recebi — reenviar
            </button>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("preview")}>
                Voltar
              </Button>
              <Button
                onClick={confirmOtp}
                disabled={signing || code.length < 4}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="attendance-confirm-otp-btn"
              >
                {signing
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                Confirmar e assinar
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
